import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { DetailedFSIResponse } from '@/services/fsi';
import { exportToPDF } from '../services/pdfExport';
import maplibregl from 'maplibre-gl';
import { 
  X, Calculator, AlertTriangle, Building, DollarSign, 
  FileText, Gavel, Home, Ruler, TrendingUp, Shield, 
  MapPin, Clock, CheckCircle, XCircle, Info, Star,
  BarChart3, PieChart, Target, Zap, Award, Activity,
  Flag, Settings, Users, Briefcase, Globe, TreePine,
  Plane, Train, Landmark, Construction, AlertCircle,
  ThumbsUp, ThumbsDown, Eye, Calendar, Archive,
  Navigation, Map, Hash, Tag, Crop, Layers, Loader2,
  Download
} from 'lucide-react';

interface DetailedFSIModalProps {
  isOpen: boolean;
  onClose: () => void;
  fsiData: DetailedFSIResponse | null;
  isLoading: boolean;
  error: string | null;
}

// Beautiful loading component with messages
const LoadingDisplay: React.FC<{ message?: string; subMessage?: string }> = ({ 
  message = "Analyzing FSI Data", 
  subMessage = "Please wait while we process comprehensive plot analysis..." 
}) => {
  const loadingMessages = [
    {
      icon: <Calculator className="h-8 w-8 text-blue-600" />,
      title: "Calculating FSI Parameters",
      subtitle: "Processing floor space index calculations and area analysis"
    },
    {
      icon: <Building className="h-8 w-8 text-green-600" />,
      title: "Analyzing Development Potential",
      subtitle: "Evaluating building regulations and height compliance"
    },
    {
      icon: <DollarSign className="h-8 w-8 text-purple-600" />,
      title: "Computing Financial Projections",
      subtitle: "Calculating costs, revenues, and profitability metrics"
    },
    {
      icon: <MapPin className="h-8 w-8 text-orange-600" />,
      title: "Processing Spatial Data",
      subtitle: "Analyzing plot geometry and road connectivity"
    },
    {
      icon: <Shield className="h-8 w-8 text-red-600" />,
      title: "Assessing Regulatory Compliance",
      subtitle: "Checking zoning requirements and approval processes"
    }
  ];

  const [currentMessageIndex, setCurrentMessageIndex] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const currentMessage = loadingMessages[currentMessageIndex];

  return (
    <div className="flex flex-col items-center justify-center py-16 px-8">
      <div className="relative mb-8">
        <div className="w-20 h-20 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
        </div>
      </div>
      
      <div className="text-center mb-6">
        <div className="flex items-center justify-center gap-3 mb-3">
          {currentMessage.icon}
          <h3 className="text-2xl font-bold text-gray-800">{currentMessage.title}</h3>
        </div>
        <p className="text-gray-600 text-lg max-w-md">{currentMessage.subtitle}</p>
      </div>
      
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Clock className="h-4 w-4" />
        <span>This may take a few seconds...</span>
      </div>
      
      <div className="mt-6 flex space-x-2">
        {loadingMessages.map((_, index) => (
          <div
            key={index}
            className={`w-2 h-2 rounded-full transition-colors duration-300 ${
              index === currentMessageIndex ? 'bg-blue-600' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

type AreaUnit = 'sqm' | 'sqft' | 'sqyard';

const DetailedFSIModal: React.FC<DetailedFSIModalProps> = ({
  isOpen,
  onClose,
  fsiData,
  isLoading,
  error
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [areaUnit, setAreaUnit] = useState<AreaUnit>('sqm');
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);

  // Conversion functions
  const convertArea = (sqm: number | undefined | null): string => {
    if (!sqm) return '0';
    switch (areaUnit) {
      case 'sqft':
        return (sqm * 10.7639).toFixed(2);
      case 'sqyard':
        return (sqm * 1.19599).toFixed(2);
      default:
        return sqm.toFixed(2);
    }
  };

  const getUnitLabel = () => {
    switch (areaUnit) {
      case 'sqft':
        return 'sq.ft';
      case 'sqyard':
        return 'sq.yd';
      default:
        return 'sqm';
    }
  };

  const formatAreaWithSecondary = (sqm: number | undefined | null, showSecondaryIn: 'sqft' | 'sqyard' = 'sqft') => {
    if (!sqm) return { primary: '0', secondary: '0', primaryUnit: getUnitLabel(), secondaryUnit: showSecondaryIn === 'sqft' ? 'sq.ft' : 'sq.yd' };
    
    const primary = convertArea(sqm);
    const secondaryValue = showSecondaryIn === 'sqft' ? (sqm * 10.7639).toFixed(2) : (sqm * 1.19599).toFixed(2);
    
    return {
      primary,
      secondary: secondaryValue,
      primaryUnit: getUnitLabel(),
      secondaryUnit: showSecondaryIn === 'sqft' ? 'sq.ft' : 'sq.yd'
    };
  };

  const tabs = [
    { id: 'executive-summary', label: 'Executive Summary', icon: Star },
    { id: 'overview', label: 'Plot Overview', icon: Home },
    { id: 'financial', label: 'Financial Analysis', icon: DollarSign },
    { id: 'fsi-analysis', label: 'FSI Analysis', icon: Calculator },
    { id: 'sba-analysis', label: 'SBA & Floor Analysis', icon: Layers },
    { id: 'regulatory', label: 'Regulatory & Compliance', icon: Gavel },
    { id: 'risk', label: 'Risk Assessment', icon: Shield },
    { id: 'spatial', label: 'Spatial Visualization', icon: MapPin },
    { id: 'recommendations', label: 'Recommendations', icon: TrendingUp },
    { id: 'tall-building', label: 'Tall Building Analysis', icon: Building },
    { id: 'development-scenarios', label: 'Development Scenarios', icon: Target }
  ];

  // Initialize map for spatial view
  useEffect(() => {
    if (activeTab === 'spatial' && mapContainerRef.current && fsiData?.polygon_data && !mapRef.current) {
      // Get center coordinates from plot polygon
      const plotCoords = fsiData.polygon_data.plot_polygon?.geometry?.coordinates?.[0]?.[0];
      let center: [number, number] = [77.5946, 12.9716]; // Default to Bangalore center
      
      if (plotCoords && plotCoords.length > 0) {
        // Calculate centroid of the polygon
        const sumX = plotCoords.reduce((sum, coord) => sum + coord[0], 0);
        const sumY = plotCoords.reduce((sum, coord) => sum + coord[1], 0);
        center = [sumX / plotCoords.length, sumY / plotCoords.length];
      }
      
      const map = new maplibregl.Map({
        container: mapContainerRef.current,
        style: {
          version: 8,
          sources: {
            'osm': {
              type: 'raster',
              tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
              tileSize: 256,
              attribution: '&copy; OpenStreetMap contributors'
            }
          },
          layers: [
            {
              id: 'osm',
              type: 'raster',
              source: 'osm'
            }
          ]
        },
        center: center,
        zoom: 16
      });

      map.on('load', () => {
        // OSM base layer is already included in the style definition
        
        // Add the plot geometry if available
        if (fsiData.polygon_data.plot_polygon) {
          map.addSource('plot', {
            type: 'geojson',
            data: fsiData.polygon_data.plot_polygon as any
          });

          map.addLayer({
            id: 'plot-fill',
            type: 'fill',
            source: 'plot',
            paint: {
              'fill-color': '#179246',
              'fill-opacity': 0.3
            }
          });

          map.addLayer({
            id: 'plot-outline',
            type: 'line',
            source: 'plot',
            paint: {
              'line-color': '#179246',
              'line-width': 3
            }
          });
        }

        // Add adjacent roads if available
        if (fsiData.polygon_data.adjacent_roads) {
          fsiData.polygon_data.adjacent_roads.forEach((road, index) => {
            map.addSource(`road-${index}`, {
              type: 'geojson',
              data: road as any
            });

            map.addLayer({
              id: `road-${index}`,
              type: 'line',
              source: `road-${index}`,
              paint: {
                'line-color': '#ff6b6b',
                'line-width': 2
              }
            });
          });
        }
      });

      mapRef.current = map;
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [activeTab, fsiData]);

  // Handle enhanced PDF export with Lucide icons
  const handleEnhancedExportPDF = async () => {
    if (!fsiData) {
      console.error('No FSI data available for export');
      return;
    }

    setIsExportingPDF(true);
    try {
      console.log('Generating professional PDF with Lucide React icons...');
      await exportToPDF(fsiData as any, `Professional_FSI_Report_${fsiData.plot_information?.name || 'Plot'}_${new Date().toISOString().split('T')[0]}.pdf`);
      console.log('Professional PDF generated successfully!');
    } catch (error) {
      console.error('Error exporting professional PDF:', error);
      alert('Failed to generate professional PDF report. Please try again.');
    } finally {
      setIsExportingPDF(false);
    }
  };

  // Handle standard PDF export
  const handleExportPDF = async () => {
    if (!fsiData) {
      console.error('No FSI data available for export');
      return;
    }

    setIsExportingPDF(true);
    try {
      await exportToPDF(fsiData as any, `FSI_Report_${fsiData.plot_information?.name || 'Plot'}_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to generate PDF report. Please try again.');
    } finally {
      setIsExportingPDF(false);
    }
  };

  if (!isOpen) return null;

  const renderTabContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#179246] mx-auto mb-4"></div>
            <p className="text-gray-600">Performing detailed FSI analysis...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-12">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Analysis Failed</h3>
          <p className="text-red-600 mb-4">{error}</p>
        </div>
      );
    }

    if (!fsiData) return null;

    switch (activeTab) {
      case 'executive-summary':
        return (
          <div className="space-y-6">
            {/* Investment Recommendation */}
            <div className={`bg-gradient-to-r ${
              fsiData.executive_summary?.investment_recommendation?.color === 'green' ? 'from-green-50 to-emerald-50' :
              fsiData.executive_summary?.investment_recommendation?.color === 'orange' ? 'from-orange-50 to-yellow-50' :
              'from-red-50 to-pink-50'
            } rounded-lg p-6`}>
              <div className="flex items-center gap-3 mb-4">
                <Award className="h-8 w-8 text-blue-600" />
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">Investment Recommendation</h3>
                  <p className="text-gray-600">Professional assessment for stakeholders</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg p-4 border shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    {fsiData.executive_summary?.investment_recommendation?.rating === 'BUY' ? 
                      <ThumbsUp className="h-5 w-5 text-green-600" /> :
                      fsiData.executive_summary?.investment_recommendation?.rating === 'CONSIDER WITH CAUTION' ?
                      <AlertTriangle className="h-5 w-5 text-orange-600" /> :
                      <ThumbsDown className="h-5 w-5 text-red-600" />
                    }
                    <span className="font-semibold">Rating</span>
                  </div>
                  <div className={`text-lg font-bold ${
                    fsiData.executive_summary?.investment_recommendation?.color === 'green' ? 'text-green-600' :
                    fsiData.executive_summary?.investment_recommendation?.color === 'orange' ? 'text-orange-600' :
                    'text-red-600'
                  }`}>
                    {fsiData.executive_summary?.investment_recommendation?.rating}
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-4 border shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="h-5 w-5 text-blue-600" />
                    <span className="font-semibold">Confidence Level</span>
                  </div>
                  <div className="text-lg font-bold text-blue-600">
                    {fsiData.executive_summary?.investment_recommendation?.confidence_level}
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-4 border shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="h-5 w-5 text-purple-600" />
                    <span className="font-semibold">ROI Projection</span>
                  </div>
                  <div className="text-lg font-bold text-purple-600">
                    {fsiData.executive_summary?.key_metrics?.projected_roi_pct?.toFixed(1)}%
                  </div>
                </div>
              </div>
            </div>

            {/* Key Metrics Dashboard */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <BarChart3 className="h-6 w-6 text-blue-600" />
                <h3 className="text-xl font-bold text-gray-800">Key Performance Metrics</h3>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <Calculator className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-blue-600">{fsiData.executive_summary?.key_metrics?.applied_fsi}</div>
                  <div className="text-sm text-gray-600">Applied FSI</div>
                </div>
                
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <Building className="h-6 w-6 text-green-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-green-600">{fsiData.executive_summary?.key_metrics?.buildable_area_sqm?.toLocaleString()}</div>
                  <div className="text-sm text-gray-600">Buildable Area (sq.m)</div>
                </div>
                
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <DollarSign className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-purple-600">₹{(fsiData.executive_summary?.key_metrics?.projected_profit / 10000000)?.toFixed(1)}Cr</div>
                  <div className="text-sm text-gray-600">Projected Profit</div>
                </div>
                
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-orange-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-orange-600">{fsiData.executive_summary?.key_metrics?.projected_roi_pct?.toFixed(1)}%</div>
                  <div className="text-sm text-gray-600">ROI</div>
                </div>
                
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <Shield className="h-6 w-6 text-red-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-red-600">{fsiData.executive_summary?.key_metrics?.risk_count}</div>
                  <div className="text-sm text-gray-600">Risk Count</div>
                </div>
                
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <Flag className="h-6 w-6 text-yellow-600 mx-auto mb-2" />
                  <div className={`text-2xl font-bold ${
                    fsiData.executive_summary?.key_metrics?.risk_level === 'LOW' ? 'text-green-600' :
                    fsiData.executive_summary?.key_metrics?.risk_level === 'MEDIUM' ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {fsiData.executive_summary?.key_metrics?.risk_level}
                  </div>
                  <div className="text-sm text-gray-600">Risk Level</div>
                </div>
              </div>
            </div>

            {/* Critical Factors & Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <AlertCircle className="h-6 w-6 text-red-500" />
                  <h4 className="font-semibold text-lg">Critical Factors</h4>
                </div>
                {fsiData.executive_summary?.critical_factors?.length ? (
                  <ul className="space-y-2">
                    {fsiData.executive_summary.critical_factors.map((factor, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span>{factor}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 text-sm">No critical factors identified</p>
                )}
              </div>
              
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <CheckCircle className="h-6 w-6 text-green-500" />
                  <h4 className="font-semibold text-lg">Immediate Actions</h4>
                </div>
                {fsiData.executive_summary?.next_immediate_actions?.length ? (
                  <ul className="space-y-2">
                    {fsiData.executive_summary.next_immediate_actions.map((action, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span>{action}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 text-sm">No immediate actions required</p>
                )}
              </div>
            </div>
          </div>
        );

      case 'overview':
      case 'overview':
        return (
          <div className="space-y-6">
            {/* Plot Summary */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Plot Information</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{fsiData.plot_information?.gid}</div>
                  <div className="text-sm text-gray-600">Plot ID (GID)</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{convertArea(fsiData.plot_information?.area_sqm)} {getUnitLabel()}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {(fsiData.plot_information?.area_sqm ? (fsiData.plot_information.area_sqm * 1.19599).toFixed(2) : '0')} sq.yd
                  </div>
                  <div className="text-sm text-gray-600">Plot Area</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">{fsiData.plot_information?.area_acres?.toFixed(2)}</div>
                  <div className="text-sm text-gray-600">Area (acres)</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">{fsiData.basic_fsi_analysis?.zone_analysis?.zone_code}</div>
                  <div className="text-sm text-gray-600">Zone Code</div>
                </div>
              </div>
            </div>

            {/* Plot Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <MapPin className="h-6 w-6 text-blue-500" />
                  <h4 className="font-semibold">Location Details</h4>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Name:</span>
                    <span className="font-medium">{fsiData.plot_information?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>FPS Code:</span>
                    <span className="font-medium">{fsiData.plot_information?.fps_code}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Village:</span>
                    <span className="font-medium">{fsiData.plot_information?.village}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>District:</span>
                    <span className="font-medium">{fsiData.plot_information?.district}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>State:</span>
                    <span className="font-medium">{fsiData.plot_information?.state}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>TPS ID:</span>
                    <span className="font-medium">{fsiData.plot_information?.tps_id}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Building className="h-6 w-6 text-green-500" />
                  <h4 className="font-semibold">Land Use Information</h4>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Land Use Type:</span>
                    <span className="font-medium">{fsiData.plot_information?.land_use_type || fsiData.basic_fsi_analysis?.land_use_analysis?.land_use_type || 'Not Specified'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Land Use Purpose:</span>
                    <span className="font-medium">{fsiData.plot_information?.land_use_purpose || 'Not Specified'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Zone Description:</span>
                    <span className="font-medium">{fsiData.basic_fsi_analysis?.zone_analysis?.zone_description}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Status:</span>
                    <span className={`font-medium px-2 py-1 rounded text-xs ${
                      fsiData.plot_information?.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {fsiData.plot_information?.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Calculator className="h-6 w-6 text-blue-500" />
                  <h4 className="font-semibold">FSI Information</h4>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Applied FSI:</span>
                    <span className="font-medium text-blue-600">{fsiData.basic_fsi_analysis?.fsi_calculations?.applied_fsi}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Base FSI:</span>
                    <span className="font-medium">{fsiData.basic_fsi_analysis?.fsi_calculations?.base_fsi}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Premium FSI:</span>
                    <span className="font-medium">{fsiData.basic_fsi_analysis?.fsi_calculations?.premium_fsi}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Max Possible FSI:</span>
                    <span className="font-medium">{fsiData.basic_fsi_analysis?.fsi_calculations?.maximum_possible_fsi}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Buildable Area:</span>
                    <span className="font-medium">{convertArea(fsiData.basic_fsi_analysis?.fsi_calculations?.gross_buildable_area_sqm)} {getUnitLabel()}</span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span></span>
                    <span>{(fsiData.basic_fsi_analysis?.fsi_calculations?.gross_buildable_area_sqm ? (fsiData.basic_fsi_analysis.fsi_calculations.gross_buildable_area_sqm * 10.7639).toFixed(2) : '0')} sq.ft</span>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <DollarSign className="h-6 w-6 text-green-500" />
                  <h4 className="font-semibold">Financial Snapshot</h4>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Development Cost:</span>
                    <span className="font-medium">₹{fsiData.basic_fsi_analysis?.financial_estimates?.total_development_cost ? (fsiData.basic_fsi_analysis.financial_estimates.total_development_cost / 10000000).toFixed(1) : '0'}Cr</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Revenue:</span>
                    <span className="font-medium">₹{fsiData.basic_fsi_analysis?.financial_estimates?.revenue?.total_revenue ? (fsiData.basic_fsi_analysis.financial_estimates.revenue.total_revenue / 10000000).toFixed(1) : '0'}Cr</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Gross Profit:</span>
                    <span className="font-medium text-green-600">₹{fsiData.basic_fsi_analysis?.financial_estimates?.profitability?.gross_profit ? (fsiData.basic_fsi_analysis.financial_estimates.profitability.gross_profit / 10000000).toFixed(1) : '0'}Cr</span>
                  </div>
                  <div className="flex justify-between">
                    <span>ROI:</span>
                    <span className="font-medium text-green-600">{fsiData.basic_fsi_analysis?.financial_estimates?.profitability?.roi_percentage?.toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Profit Margin:</span>
                    <span className="font-medium">{fsiData.basic_fsi_analysis?.financial_estimates?.profitability?.margin_percentage?.toFixed(1)}%</span>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Ruler className="h-6 w-6 text-purple-500" />
                  <h4 className="font-semibold">Road Analysis</h4>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Road Width:</span>
                    <span className="font-medium">{fsiData.basic_fsi_analysis?.road_analysis?.road_width_m?.toFixed(1)}m</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Road Category:</span>
                    <span className="font-medium">{fsiData.basic_fsi_analysis?.road_analysis?.road_category}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Premium %:</span>
                    <span className="font-medium">{fsiData.basic_fsi_analysis?.road_analysis?.premium_percentage}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tall Building Eligible:</span>
                    <span className={`font-medium ${fsiData.basic_fsi_analysis?.road_analysis?.tall_building_eligible ? 'text-green-600' : 'text-red-600'}`}>
                      {fsiData.basic_fsi_analysis?.road_analysis?.tall_building_eligible ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Height Compliance */}
            {fsiData.basic_fsi_analysis?.height_compliance && (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Building className="h-6 w-6 text-blue-500" />
                  <h4 className="font-semibold">Height Compliance Analysis</h4>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="flex justify-between">
                    <span>Max Height Allowed:</span>
                    <span className="font-medium">{fsiData.basic_fsi_analysis.height_compliance.max_height_allowed_m}m</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Building Height:</span>
                    <span className="font-medium">{fsiData.basic_fsi_analysis.height_compliance.building_height_m}m</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Compliance:</span>
                    <span className={`font-medium ${fsiData.basic_fsi_analysis.height_compliance.height_compliant ? 'text-green-600' : 'text-red-600'}`}>
                      {fsiData.basic_fsi_analysis.height_compliance.height_compliant ? 'Compliant' : 'Non-Compliant'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>SEOR Requirement:</span>
                    <span className="font-medium">{fsiData.basic_fsi_analysis.height_compliance.seor_requirement}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'financial':
        return (
          <div className="space-y-6">
            {/* Financial Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-lg p-6 border border-green-200">
                <div className="flex items-center gap-3 mb-4">
                  <DollarSign className="h-8 w-8 text-green-600" />
                  <div>
                    <h3 className="text-lg font-bold text-green-800">Total Revenue</h3>
                    <p className="text-green-600 text-sm">Gross revenue projection</p>
                  </div>
                </div>
                <div className="text-3xl font-bold text-green-700">
                  ₹{fsiData.enhanced_financial_analysis?.revenue_analysis?.gross_revenue ? (fsiData.enhanced_financial_analysis.revenue_analysis.gross_revenue / 10000000).toFixed(1) : '0'}Cr
                </div>
                <div className="text-sm text-green-600 mt-2">
                  Rate: ₹{fsiData.enhanced_financial_analysis?.revenue_analysis?.market_rate_per_sqm?.toLocaleString()}/sq.m
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                <div className="flex items-center gap-3 mb-4">
                  <Construction className="h-8 w-8 text-blue-600" />
                  <div>
                    <h3 className="text-lg font-bold text-blue-800">Development Cost</h3>
                    <p className="text-blue-600 text-sm">Total project cost</p>
                  </div>
                </div>
                <div className="text-3xl font-bold text-blue-700">
                  ₹{fsiData.enhanced_financial_analysis?.detailed_costs?.total_development_cost ? (fsiData.enhanced_financial_analysis.detailed_costs.total_development_cost / 10000000).toFixed(1) : '0'}Cr
                </div>
                <div className="text-sm text-blue-600 mt-2">
                  Per sq.m: ₹{fsiData.enhanced_financial_analysis?.profitability_metrics?.cost_per_saleable_sqm?.toLocaleString()}
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6 border border-purple-200">
                <div className="flex items-center gap-3 mb-4">
                  <TrendingUp className="h-8 w-8 text-purple-600" />
                  <div>
                    <h3 className="text-lg font-bold text-purple-800">Net Profit</h3>
                    <p className="text-purple-600 text-sm">Projected profit</p>
                  </div>
                </div>
                <div className="text-3xl font-bold text-purple-700">
                  ₹{fsiData.enhanced_financial_analysis?.profitability_metrics?.gross_profit ? (fsiData.enhanced_financial_analysis.profitability_metrics.gross_profit / 10000000).toFixed(1) : '0'}Cr
                </div>
                <div className="text-sm text-purple-600 mt-2">
                  Margin: {fsiData.enhanced_financial_analysis?.profitability_metrics?.profit_margin_pct?.toFixed(1)}%
                </div>
              </div>
            </div>

            {/* Detailed Cost Breakdown */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <PieChart className="h-6 w-6 text-blue-600" />
                <h3 className="text-xl font-bold text-gray-800">Detailed Cost Breakdown</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Building className="h-5 w-5 text-gray-600" />
                    <span className="font-semibold">Construction</span>
                  </div>
                  <div className="text-lg font-bold text-gray-700">
                    ₹{fsiData.enhanced_financial_analysis?.detailed_costs?.construction?.total_cost ? (fsiData.enhanced_financial_analysis.detailed_costs.construction.total_cost / 10000000).toFixed(1) : '0'}Cr
                  </div>
                  <div className="text-sm text-gray-600">
                    Rate: ₹{fsiData.enhanced_financial_analysis?.detailed_costs?.construction?.rate_per_sqm?.toLocaleString()}/sq.m
                  </div>
                  <div className="text-sm text-gray-600">
                    Area: {fsiData.enhanced_financial_analysis?.detailed_costs?.construction?.area_sqm?.toLocaleString()} sq.m
                  </div>
                </div>

                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Settings className="h-5 w-5 text-orange-600" />
                    <span className="font-semibold">Infrastructure</span>
                  </div>
                  <div className="text-lg font-bold text-orange-700">
                    ₹{fsiData.enhanced_financial_analysis?.detailed_costs?.infrastructure?.total_cost ? (fsiData.enhanced_financial_analysis.detailed_costs.infrastructure.total_cost / 10000000).toFixed(1) : '0'}Cr
                  </div>
                  <div className="text-sm text-orange-600">
                    Rate: ₹{fsiData.enhanced_financial_analysis?.detailed_costs?.infrastructure?.rate_per_sqm?.toLocaleString()}/sq.m
                  </div>
                </div>

                <div className="bg-yellow-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-5 w-5 text-yellow-600" />
                    <span className="font-semibold">Professional Fees</span>
                  </div>
                  <div className="text-lg font-bold text-yellow-700">
                    ₹{fsiData.enhanced_financial_analysis?.detailed_costs?.professional_fees ? (fsiData.enhanced_financial_analysis.detailed_costs.professional_fees / 10000000).toFixed(1) : '0'}Cr
                  </div>
                </div>

                <div className="bg-red-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-5 w-5 text-red-600" />
                    <span className="font-semibold">Statutory Charges</span>
                  </div>
                  <div className="text-lg font-bold text-red-700">
                    ₹{fsiData.enhanced_financial_analysis?.detailed_costs?.statutory_charges?.total ? (fsiData.enhanced_financial_analysis.detailed_costs.statutory_charges.total / 100000).toFixed(1) : '0'}L
                  </div>
                  <div className="text-xs text-red-600">
                    <div>FSI: ₹{fsiData.enhanced_financial_analysis?.detailed_costs?.statutory_charges?.additional_fsi_cost ? (fsiData.enhanced_financial_analysis.detailed_costs.statutory_charges.additional_fsi_cost / 100000).toFixed(1) : '0'}L</div>
                    <div>Scrutiny: ₹{fsiData.enhanced_financial_analysis?.detailed_costs?.statutory_charges?.scrutiny_fees ? (fsiData.enhanced_financial_analysis.detailed_costs.statutory_charges.scrutiny_fees / 1000).toFixed(0) : '0'}K</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Revenue Analysis */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <BarChart3 className="h-6 w-6 text-green-600" />
                <h3 className="text-xl font-bold text-gray-800">Revenue Analysis</h3>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <Building className="h-6 w-6 text-green-600 mx-auto mb-2" />
                  <div className="text-xl font-bold text-green-600">{convertArea(fsiData.enhanced_financial_analysis?.revenue_analysis?.buildable_area_sqm)} {getUnitLabel()}</div>
                  <div className="text-xs text-gray-500">
                    {(fsiData.enhanced_financial_analysis?.revenue_analysis?.buildable_area_sqm ? (fsiData.enhanced_financial_analysis.revenue_analysis.buildable_area_sqm * 10.7639).toFixed(2) : '0')} sq.ft
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Buildable Area</div>
                </div>
                
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <Home className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                  <div className="text-xl font-bold text-blue-600">{convertArea(fsiData.enhanced_financial_analysis?.revenue_analysis?.saleable_area_sqm)} {getUnitLabel()}</div>
                  <div className="text-xs text-gray-500">
                    {(fsiData.enhanced_financial_analysis?.revenue_analysis?.saleable_area_sqm ? (fsiData.enhanced_financial_analysis.revenue_analysis.saleable_area_sqm * 10.7639).toFixed(2) : '0')} sq.ft
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Saleable Area</div>
                </div>
                
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <Activity className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                  <div className="text-xl font-bold text-purple-600">{(fsiData.enhanced_financial_analysis?.revenue_analysis?.saleable_ratio * 100)?.toFixed(1)}%</div>
                  <div className="text-sm text-gray-600">Saleable Ratio</div>
                </div>
                
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <DollarSign className="h-6 w-6 text-orange-600 mx-auto mb-2" />
                  <div className="text-xl font-bold text-orange-600">
                    ₹{fsiData.enhanced_financial_analysis?.revenue_analysis?.market_rate_per_sqm ? 
                      (areaUnit === 'sqm' ? fsiData.enhanced_financial_analysis.revenue_analysis.market_rate_per_sqm :
                       areaUnit === 'sqft' ? (fsiData.enhanced_financial_analysis.revenue_analysis.market_rate_per_sqm / 10.7639) :
                       (fsiData.enhanced_financial_analysis.revenue_analysis.market_rate_per_sqm / 1.19599)).toFixed(0).toLocaleString() : '0'}
                  </div>
                  <div className="text-sm text-gray-600">Rate per {getUnitLabel()}</div>
                </div>
              </div>
            </div>

            {/* Profitability Metrics */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <Target className="h-6 w-6 text-purple-600" />
                <h3 className="text-xl font-bold text-gray-800">Profitability Metrics</h3>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                  <div className="text-xl font-bold text-purple-600">{fsiData.enhanced_financial_analysis?.profitability_metrics?.roi_pct?.toFixed(1)}%</div>
                  <div className="text-sm text-gray-600">ROI</div>
                </div>
                
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <PieChart className="h-6 w-6 text-green-600 mx-auto mb-2" />
                  <div className="text-xl font-bold text-green-600">{fsiData.enhanced_financial_analysis?.profitability_metrics?.profit_margin_pct?.toFixed(1)}%</div>
                  <div className="text-sm text-gray-600">Profit Margin</div>
                </div>
                
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <Activity className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                  <div className="text-xl font-bold text-blue-600">{fsiData.enhanced_financial_analysis?.profitability_metrics?.revenue_multiple?.toFixed(1)}x</div>
                  <div className="text-sm text-gray-600">Revenue Multiple</div>
                </div>
                
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <DollarSign className="h-6 w-6 text-yellow-600 mx-auto mb-2" />
                  <div className="text-xl font-bold text-yellow-600">
                    ₹{fsiData.enhanced_financial_analysis?.profitability_metrics?.cost_per_saleable_sqm ? 
                      (areaUnit === 'sqm' ? fsiData.enhanced_financial_analysis.profitability_metrics.cost_per_saleable_sqm :
                       areaUnit === 'sqft' ? (fsiData.enhanced_financial_analysis.profitability_metrics.cost_per_saleable_sqm / 10.7639) :
                       (fsiData.enhanced_financial_analysis.profitability_metrics.cost_per_saleable_sqm / 1.19599)).toFixed(0).toLocaleString() : '0'}
                  </div>
                  <div className="text-sm text-gray-600">Cost per Saleable {getUnitLabel()}</div>
                </div>
                
                <div className="text-center p-4 bg-orange-50 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-orange-600 mx-auto mb-2" />
                  <div className="text-xl font-bold text-orange-600">₹{fsiData.enhanced_financial_analysis?.profitability_metrics?.gross_profit ? (fsiData.enhanced_financial_analysis.profitability_metrics.gross_profit / 10000000).toFixed(1) : '0'}Cr</div>
                  <div className="text-sm text-gray-600">Gross Profit</div>
                </div>
              </div>
            </div>

            {/* Sensitivity Analysis */}
            {fsiData.enhanced_financial_analysis?.sensitivity_analysis && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <BarChart3 className="h-6 w-6 text-red-600" />
                  <h3 className="text-xl font-bold text-gray-800">Sensitivity Analysis</h3>
                  <span className="text-sm text-gray-500">Impact of cost and rate variations</span>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Scenario</th>
                        <th className="text-center p-2">Cost Variation</th>
                        <th className="text-center p-2">Rate Variation</th>
                        <th className="text-center p-2">Profit (₹Cr)</th>
                        <th className="text-center p-2">ROI (%)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(fsiData.enhanced_financial_analysis.sensitivity_analysis).slice(0, 6).map(([scenario, data]) => (
                        <tr key={scenario} className="border-b hover:bg-gray-50">
                          <td className="p-2 font-medium">{scenario.replace(/_/g, ' ')}</td>
                          <td className="text-center p-2">{data.cost_variation_pct}%</td>
                          <td className="text-center p-2">{data.rate_variation_pct}%</td>
                          <td className={`text-center p-2 font-medium ${data.profit > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            ₹{(data.profit / 10000000).toFixed(1)}
                          </td>
                          <td className={`text-center p-2 font-medium ${data.roi_pct > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {data.roi_pct.toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );

      case 'regulatory':
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Regulatory Compliance</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Gavel className="h-6 w-6 text-purple-500" />
                    <h4 className="font-semibold">Zone Information</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Zone Code:</span>
                      <span className="font-medium">{fsiData.basic_fsi_analysis?.zone_analysis?.zone_code}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Zone Description:</span>
                      <span className="font-medium">{fsiData.basic_fsi_analysis?.zone_analysis?.zone_description || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Land Use Type:</span>
                      <span className="font-medium">{fsiData.plot_information?.land_use_type || 'N/A'}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <FileText className="h-6 w-6 text-blue-500" />
                    <h4 className="font-semibold">Approvals Required</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Core Approvals:</span>
                      <span className="font-medium">{fsiData.detailed_sections?.regulatory_analysis?.core_approvals_needed?.length || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Additional Requirements:</span>
                      <span className="font-medium">{fsiData.detailed_sections?.regulatory_analysis?.additional_requirements?.length || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Timeline (months):</span>
                      <span className="font-medium">{fsiData.detailed_sections?.regulatory_analysis?.timeline_estimates?.total_timeline_months || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {fsiData.overlay_analysis?.clearances_required && fsiData.overlay_analysis.clearances_required.length > 0 && (
                <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-800 mb-2">Special Clearances Required</h4>
                  <ul className="space-y-1 text-sm text-yellow-700">
                    {fsiData.overlay_analysis.clearances_required.map((clearance, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        {clearance}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        );

      case 'spatial':
        return (
          <div className="space-y-6">
            {fsiData.polygon_data || fsiData.detailed_sections?.spatial_visualization ? (
              <>
                {/* Spatial Overview */}
                <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <MapPin className="h-8 w-8 text-cyan-600" />
                    <div>
                      <h3 className="text-2xl font-bold text-gray-800">Spatial Visualization</h3>
                      <p className="text-gray-600">Plot geometry and surrounding context analysis</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg p-4 text-center">
                      <MapPin className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                      <div className="text-lg font-bold text-blue-600">
                        {(fsiData.polygon_data?.summary || fsiData.detailed_sections?.spatial_visualization?.summary)?.plot_available ? 'Available' : 'N/A'}
                      </div>
                      <div className="text-sm text-gray-600">Plot Geometry</div>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4 text-center">
                      <Navigation className="h-6 w-6 text-green-600 mx-auto mb-2" />
                      <div className="text-lg font-bold text-green-600">
                        {(fsiData.polygon_data?.summary || fsiData.detailed_sections?.spatial_visualization?.summary)?.adjacent_roads_count || 0}
                      </div>
                      <div className="text-sm text-gray-600">Adjacent Roads</div>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4 text-center">
                      <Globe className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                      <div className="text-lg font-bold text-purple-600">
                        {(fsiData.polygon_data?.summary || fsiData.detailed_sections?.spatial_visualization?.summary)?.coordinate_system || 'WGS84'}
                      </div>
                      <div className="text-sm text-gray-600">Coordinate System</div>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4 text-center">
                      <Eye className="h-6 w-6 text-orange-600 mx-auto mb-2" />
                      <div className="text-lg font-bold text-orange-600">
                        {(fsiData.polygon_data?.summary || fsiData.detailed_sections?.spatial_visualization?.summary)?.visualization_buffer_available ? 'Available' : 'N/A'}
                      </div>
                      <div className="text-sm text-gray-600">Buffer Zone</div>
                    </div>
                  </div>
                </div>

                {/* Interactive Map */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Map className="h-6 w-6 text-green-600" />
                    <h3 className="text-xl font-bold text-gray-800">Interactive Map View</h3>
                  </div>
                  
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                    <div 
                      ref={mapContainerRef} 
                      className="w-full h-96 rounded-lg relative"
                      style={{ minHeight: '400px' }}
                    />
                    {/* Map Attribution for OSM */}
                    <div className="absolute bottom-0 right-0 bg-white bg-opacity-90 px-2 py-1 text-xs text-gray-600 rounded-tl">
                      © OpenStreetMap contributors
                    </div>
                  </div>
                  
                  {/* Map Legend */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div className="bg-blue-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-4 h-4 bg-[#179246] opacity-30 rounded"></div>
                        <span className="font-medium">Plot Boundary</span>
                      </div>
                      <p className="text-gray-600 text-sm">Main plot polygon with calculated area</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-4 h-1 bg-red-500 rounded"></div>
                        <span className="font-medium">Adjacent Roads</span>
                      </div>
                      <p className="text-gray-600 text-sm">Roads touching the plot boundary</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-4 h-1 bg-green-500 rounded border-dashed border-2 border-green-500"></div>
                        <span className="font-medium">Buffer Zone</span>
                      </div>
                      <p className="text-gray-600 text-sm">Analysis buffer area</p>
                    </div>
                  </div>
                </div>

                {/* Plot Information */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <MapPin className="h-6 w-6 text-blue-600" />
                    <h3 className="text-xl font-bold text-gray-800">Plot Geometry Details</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <Layers className="h-5 w-5 text-blue-600" />
                        <span className="font-medium">Geometry Type:</span>
                        <span>{(fsiData.polygon_data?.plot_polygon || fsiData.detailed_sections?.spatial_visualization?.plot_polygon)?.geometry?.type || 'N/A'}</span>
                      </div>
                      
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <Calculator className="h-5 w-5 text-green-600" />
                          <span className="font-medium">Area:</span>
                          <span>{convertArea((fsiData.polygon_data?.plot_polygon || fsiData.detailed_sections?.spatial_visualization?.plot_polygon)?.properties?.area_sqm)} {getUnitLabel()}</span>
                        </div>
                        <div className="text-xs text-gray-500 ml-7">
                          {((fsiData.polygon_data?.plot_polygon || fsiData.detailed_sections?.spatial_visualization?.plot_polygon)?.properties?.area_sqm ? ((fsiData.polygon_data?.plot_polygon || fsiData.detailed_sections?.spatial_visualization?.plot_polygon)?.properties?.area_sqm * 1.19599).toFixed(2) : '0')} sq.yd
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-purple-600" />
                        <span className="font-medium">Village:</span>
                        <span>{(fsiData.polygon_data?.plot_polygon || fsiData.detailed_sections?.spatial_visualization?.plot_polygon)?.properties?.village || 'N/A'}</span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Building className="h-5 w-5 text-orange-600" />
                        <span className="font-medium">TPS ID:</span>
                        <span>{(fsiData.polygon_data?.plot_polygon || fsiData.detailed_sections?.spatial_visualization?.plot_polygon)?.properties?.tps_id || 'N/A'}</span>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <Hash className="h-5 w-5 text-indigo-600" />
                        <span className="font-medium">GID:</span>
                        <span>{(fsiData.polygon_data?.plot_polygon || fsiData.detailed_sections?.spatial_visualization?.plot_polygon)?.properties?.gid || 'N/A'}</span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Tag className="h-5 w-5 text-red-600" />
                        <span className="font-medium">Land Use:</span>
                        <span>{(fsiData.polygon_data?.plot_polygon || fsiData.detailed_sections?.spatial_visualization?.plot_polygon)?.properties?.land_use_type || 'Not Specified'}</span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Target className="h-5 w-5 text-cyan-600" />
                        <span className="font-medium">Plot Type:</span>
                        <span>{(fsiData.polygon_data?.plot_polygon || fsiData.detailed_sections?.spatial_visualization?.plot_polygon)?.properties?.type || 'N/A'}</span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Crop className="h-5 w-5 text-yellow-600" />
                        <span className="font-medium">Area (Acres):</span>
                        <span>{(fsiData.polygon_data?.plot_polygon || fsiData.detailed_sections?.spatial_visualization?.plot_polygon)?.properties?.area_acres?.toFixed(3)} acres</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Adjacent Roads Analysis */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Navigation className="h-6 w-6 text-green-600" />
                    <h3 className="text-xl font-bold text-gray-800">Adjacent Roads Analysis</h3>
                  </div>
                  
                  {(fsiData.polygon_data?.adjacent_roads || fsiData.detailed_sections?.spatial_visualization?.adjacent_roads)?.length ? (
                    <div className="space-y-4">
                      {(fsiData.polygon_data?.adjacent_roads || fsiData.detailed_sections?.spatial_visualization?.adjacent_roads).map((road, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Hash className="h-4 w-4 text-blue-600" />
                                <span className="font-medium">Road ID:</span>
                                <span>{road.properties?.road_id || 'N/A'}</span>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                <Ruler className="h-4 w-4 text-green-600" />
                                <span className="font-medium">Width:</span>
                                <span>{road.properties?.width_m?.toFixed(2)}m</span>
                              </div>
                            </div>
                            
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <MapPin className="h-4 w-4 text-purple-600" />
                                <span className="font-medium">Distance:</span>
                                <span>{road.properties?.distance_to_plot?.toFixed(1)}m</span>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                <Navigation className="h-4 w-4 text-orange-600" />
                                <span className="font-medium">Adjacent:</span>
                                <span className={road.properties?.is_adjacent ? 'text-green-600' : 'text-red-600'}>
                                  {road.properties?.is_adjacent ? 'Yes' : 'No'}
                                </span>
                              </div>
                            </div>
                            
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <Layers className="h-4 w-4 text-indigo-600" />
                                <span className="font-medium">Type:</span>
                                <span>{road.properties?.type || 'adjacent_road'}</span>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                <Target className="h-4 w-4 text-cyan-600" />
                                <span className="font-medium">Geometry:</span>
                                <span>{road.geometry?.type || 'N/A'}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Navigation className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500">No adjacent roads data available</p>
                    </div>
                  )}
                </div>

                {/* Visualization Buffer */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Eye className="h-6 w-6 text-orange-600" />
                    <h3 className="text-xl font-bold text-gray-800">Visualization Context</h3>
                  </div>
                  
                  {(fsiData.polygon_data?.visualization_buffer || fsiData.detailed_sections?.spatial_visualization?.visualization_buffer) ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Target className="h-5 w-5 text-orange-600" />
                          <span className="font-medium">Buffer Distance:</span>
                          <span>{(fsiData.polygon_data?.visualization_buffer || fsiData.detailed_sections?.spatial_visualization?.visualization_buffer)?.properties?.buffer_distance_m}m</span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Eye className="h-5 w-5 text-purple-600" />
                          <span className="font-medium">Purpose:</span>
                          <span className="capitalize">{(fsiData.polygon_data?.visualization_buffer || fsiData.detailed_sections?.spatial_visualization?.visualization_buffer)?.properties?.purpose?.replace(/_/g, ' ') || 'N/A'}</span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Layers className="h-5 w-5 text-blue-600" />
                          <span className="font-medium">Zone Type:</span>
                          <span className="capitalize">{(fsiData.polygon_data?.visualization_buffer || fsiData.detailed_sections?.spatial_visualization?.visualization_buffer)?.properties?.type?.replace(/_/g, ' ') || 'N/A'}</span>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Globe className="h-5 w-5 text-green-600" />
                          <span className="font-medium">Geometry Type:</span>
                          <span>{(fsiData.polygon_data?.visualization_buffer || fsiData.detailed_sections?.spatial_visualization?.visualization_buffer)?.geometry?.type || 'N/A'}</span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Calculator className="h-5 w-5 text-indigo-600" />
                          <span className="font-medium">Coordinates Available:</span>
                          <span className="text-green-600">
                            {(fsiData.polygon_data?.visualization_buffer || fsiData.detailed_sections?.spatial_visualization?.visualization_buffer)?.geometry?.coordinates ? 'Yes' : 'No'}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <MapPin className="h-5 w-5 text-red-600" />
                          <span className="font-medium">Context Analysis:</span>
                          <span className="text-green-600">Available</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Eye className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500">No visualization buffer data available</p>
                    </div>
                  )}
                </div>

                {/* Spatial Analysis Summary */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <BarChart3 className="h-6 w-6 text-blue-600" />
                    <h3 className="text-xl font-bold text-gray-800">Spatial Analysis Summary</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-4">
                      <h4 className="font-semibold text-gray-800 mb-3">Connectivity Analysis</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Road Access:</span>
                          <span className="font-medium text-green-600">Direct Access Available</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Road Width Category:</span>
                          <span className="font-medium text-blue-600">
                            {(fsiData.polygon_data?.adjacent_roads?.[0] || fsiData.detailed_sections?.spatial_visualization?.adjacent_roads?.[0])?.properties?.width_m >= 18 ? 'Wide Road (≥18m)' : 'Standard Road'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Accessibility Score:</span>
                          <span className="font-medium text-green-600">High</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4">
                      <h4 className="font-semibold text-gray-800 mb-3">Location Advantages</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span>Well-defined plot boundaries</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span>Clear spatial coordinates</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span>Adjacent road connectivity</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span>Suitable for development</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <MapPin className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-600 mb-2">Spatial Data Not Available</h3>
                <p className="text-gray-500">Spatial visualization data is not available for this plot.</p>
              </div>
            )}
          </div>
        );

      case 'fsi-analysis':
        return (
          <div className="space-y-6">
            {/* FSI Calculations Overview */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <Calculator className="h-8 w-8 text-blue-600" />
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">FSI Analysis</h3>
                  <p className="text-gray-600">Comprehensive Floor Space Index calculations</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <div className="text-3xl font-bold text-blue-600">{fsiData.basic_fsi_analysis?.fsi_calculations?.applied_fsi}</div>
                  <div className="text-sm text-gray-600">Applied FSI</div>
                  <div className="text-xs text-gray-500 mt-1">Current utilization</div>
                </div>
                
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <div className="text-3xl font-bold text-green-600">{fsiData.basic_fsi_analysis?.fsi_calculations?.base_fsi}</div>
                  <div className="text-sm text-gray-600">Base FSI</div>
                  <div className="text-xs text-gray-500 mt-1">Zone entitlement</div>
                </div>
                
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <div className="text-3xl font-bold text-orange-600">{fsiData.basic_fsi_analysis?.fsi_calculations?.premium_fsi}</div>
                  <div className="text-sm text-gray-600">Premium FSI</div>
                  <div className="text-xs text-gray-500 mt-1">Additional chargeable</div>
                </div>
                
                <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                  <div className="text-3xl font-bold text-purple-600">{fsiData.basic_fsi_analysis?.fsi_calculations?.maximum_possible_fsi}</div>
                  <div className="text-sm text-gray-600">Max Possible FSI</div>
                  <div className="text-xs text-gray-500 mt-1">Total achievable</div>
                </div>
              </div>
            </div>

            {/* Area Calculations */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <Ruler className="h-6 w-6 text-green-600" />
                <h3 className="text-xl font-bold text-gray-800">Area Calculations</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="h-5 w-5 text-gray-600" />
                    <span className="font-semibold">Plot Areas</span>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div>
                      <div className="flex justify-between">
                        <span>Gross Plot Area:</span>
                        <span className="font-medium">{convertArea(fsiData.basic_fsi_analysis?.fsi_calculations?.gross_plot_area_sqm)} {getUnitLabel()}</span>
                      </div>
                      <div className="flex justify-end text-xs text-gray-500">
                        {(fsiData.basic_fsi_analysis?.fsi_calculations?.gross_plot_area_sqm ? (fsiData.basic_fsi_analysis.fsi_calculations.gross_plot_area_sqm * 1.19599).toFixed(2) : '0')} sq.yd
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between">
                        <span>Net Developable Area:</span>
                        <span className="font-medium">{convertArea(fsiData.basic_fsi_analysis?.fsi_calculations?.net_developable_area_sqm)} {getUnitLabel()}</span>
                      </div>
                      <div className="flex justify-end text-xs text-gray-500">
                        {(fsiData.basic_fsi_analysis?.fsi_calculations?.net_developable_area_sqm ? (fsiData.basic_fsi_analysis.fsi_calculations.net_developable_area_sqm * 1.19599).toFixed(2) : '0')} sq.yd
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between">
                        <span>TP Deduction:</span>
                        <span className="font-medium">{convertArea(fsiData.basic_fsi_analysis?.fsi_calculations?.tp_deduction_area_sqm)} {getUnitLabel()} ({fsiData.basic_fsi_analysis?.fsi_calculations?.tp_deduction_pct}%)</span>
                      </div>
                      <div className="flex justify-end text-xs text-gray-500">
                        {(fsiData.basic_fsi_analysis?.fsi_calculations?.tp_deduction_area_sqm ? (fsiData.basic_fsi_analysis.fsi_calculations.tp_deduction_area_sqm * 1.19599).toFixed(2) : '0')} sq.yd
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Building className="h-5 w-5 text-blue-600" />
                    <span className="font-semibold">Buildable Areas</span>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div>
                      <div className="flex justify-between">
                        <span>Gross Buildable Area:</span>
                        <span className="font-medium">{convertArea(fsiData.basic_fsi_analysis?.fsi_calculations?.gross_buildable_area_sqm)} {getUnitLabel()}</span>
                      </div>
                      <div className="flex justify-end text-xs text-gray-500">
                        {(fsiData.basic_fsi_analysis?.fsi_calculations?.gross_buildable_area_sqm ? (fsiData.basic_fsi_analysis.fsi_calculations.gross_buildable_area_sqm * 10.7639).toFixed(2) : '0')} sq.ft
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between">
                        <span>Maximum Buildable:</span>
                        <span className="font-medium">{convertArea(fsiData.basic_fsi_analysis?.fsi_calculations?.maximum_buildable_area_sqm)} {getUnitLabel()}</span>
                      </div>
                      <div className="flex justify-end text-xs text-gray-500">
                        {(fsiData.basic_fsi_analysis?.fsi_calculations?.maximum_buildable_area_sqm ? (fsiData.basic_fsi_analysis.fsi_calculations.maximum_buildable_area_sqm * 10.7639).toFixed(2) : '0')} sq.ft
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between">
                        <span>Max Ground Floor:</span>
                        <span className="font-medium">{convertArea(fsiData.basic_fsi_analysis?.fsi_calculations?.max_ground_floor_area_sqm)} {getUnitLabel()}</span>
                      </div>
                      <div className="flex justify-end text-xs text-gray-500">
                        {(fsiData.basic_fsi_analysis?.fsi_calculations?.max_ground_floor_area_sqm ? (fsiData.basic_fsi_analysis.fsi_calculations.max_ground_floor_area_sqm * 10.7639).toFixed(2) : '0')} sq.ft
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Activity className="h-5 w-5 text-green-600" />
                    <span className="font-semibold">Efficiency Metrics</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Development Efficiency:</span>
                      <span className="font-medium">{fsiData.basic_fsi_analysis?.fsi_calculations?.development_efficiency_pct}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>FSI Utilization:</span>
                      <span className="font-medium text-green-600">
                        {fsiData.basic_fsi_analysis?.fsi_calculations?.applied_fsi && fsiData.basic_fsi_analysis?.fsi_calculations?.maximum_possible_fsi 
                          ? ((fsiData.basic_fsi_analysis.fsi_calculations.applied_fsi / fsiData.basic_fsi_analysis.fsi_calculations.maximum_possible_fsi) * 100).toFixed(1)
                          : '0'}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Area Efficiency:</span>
                      <span className="font-medium">
                        {fsiData.plot_information?.area_sqm && fsiData.basic_fsi_analysis?.fsi_calculations?.gross_buildable_area_sqm
                          ? ((fsiData.basic_fsi_analysis.fsi_calculations.gross_buildable_area_sqm / fsiData.plot_information.area_sqm) * 100).toFixed(1)
                          : '0'}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Chargeable FSI Analysis */}
            {fsiData.basic_fsi_analysis?.chargeable_fsi_analysis && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <DollarSign className="h-6 w-6 text-orange-600" />
                  <h3 className="text-xl font-bold text-gray-800">Chargeable FSI Analysis</h3>
                  <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-medium">
                    {fsiData.basic_fsi_analysis.chargeable_fsi_analysis.applicable_schedule}
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-orange-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Calculator className="h-5 w-5 text-orange-600" />
                      <span className="font-semibold">Max Chargeable FSI</span>
                    </div>
                    <div className="text-2xl font-bold text-orange-600">{fsiData.basic_fsi_analysis.chargeable_fsi_analysis.max_chargeable_fsi}</div>
                    <div className="text-sm text-orange-700">({fsiData.basic_fsi_analysis.chargeable_fsi_analysis.max_chargeable_fsi_pct}% of base)</div>
                  </div>

                  <div className="bg-red-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="h-5 w-5 text-red-600" />
                      <span className="font-semibold">Charge Rate</span>
                    </div>
                    <div className="text-2xl font-bold text-red-600">{fsiData.basic_fsi_analysis.chargeable_fsi_analysis.charge_rate_pct_of_jantri}%</div>
                    <div className="text-sm text-red-700">of Jantri rate</div>
                  </div>

                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TreePine className="h-5 w-5 text-green-600" />
                      <span className="font-semibold">Green Building Exemption</span>
                    </div>
                    <div className="text-lg font-bold text-green-600">
                      {fsiData.basic_fsi_analysis.chargeable_fsi_analysis.green_building_exemption_min_pct}% - {fsiData.basic_fsi_analysis.chargeable_fsi_analysis.green_building_exemption_max_pct}%
                    </div>
                    <div className="text-sm text-green-700">Available discount</div>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Info className="h-5 w-5 text-blue-600" />
                      <span className="font-semibold">Road Impact</span>
                    </div>
                    <div className="text-sm text-blue-700">
                      {fsiData.basic_fsi_analysis.chargeable_fsi_analysis.road_width_impact}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Land Use Analysis */}
            {fsiData.basic_fsi_analysis?.land_use_analysis && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Globe className="h-6 w-6 text-blue-600" />
                  <h3 className="text-xl font-bold text-gray-800">Land Use Analysis</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="font-semibold mb-2 flex items-center gap-2">
                        <Building className="h-5 w-5 text-blue-600" />
                        Land Use Details
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Type:</span>
                          <span className="font-medium">{fsiData.basic_fsi_analysis.land_use_analysis.land_use_type}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Purpose:</span>
                          <span className="font-medium">{fsiData.basic_fsi_analysis.land_use_analysis.land_use_purpose || 'Not specified'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>FSI Source:</span>
                          <span className="font-medium">{fsiData.basic_fsi_analysis.land_use_analysis.fsi_source}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Effective Base FSI:</span>
                          <span className="font-medium">{fsiData.basic_fsi_analysis.land_use_analysis.effective_base_fsi}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 rounded-lg p-4">
                      <h4 className="font-semibold mb-2 flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        Compliance Status
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>FSI Compliant:</span>
                          <span className={`font-medium ${fsiData.basic_fsi_analysis.land_use_analysis.fsi_compliant ? 'text-green-600' : 'text-red-600'}`}>
                            {fsiData.basic_fsi_analysis.land_use_analysis.fsi_compliant ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Land Use Compliant:</span>
                          <span className={`font-medium ${fsiData.basic_fsi_analysis.land_use_analysis.land_use_compliance?.compliant ? 'text-green-600' : 'text-red-600'}`}>
                            {fsiData.basic_fsi_analysis.land_use_analysis.land_use_compliance?.compliant ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Premium Applicable:</span>
                          <span className={`font-medium ${fsiData.basic_fsi_analysis.land_use_analysis.premium_applicable ? 'text-green-600' : 'text-red-600'}`}>
                            {fsiData.basic_fsi_analysis.land_use_analysis.premium_applicable ? 'Yes' : 'No'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Development Charges:</span>
                          <span className={`font-medium ${fsiData.basic_fsi_analysis.land_use_analysis.development_charges_applicable ? 'text-orange-600' : 'text-green-600'}`}>
                            {fsiData.basic_fsi_analysis.land_use_analysis.development_charges_applicable ? 'Applicable' : 'Not Applicable'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-purple-50 rounded-lg p-4">
                      <h4 className="font-semibold mb-2 flex items-center gap-2">
                        <Target className="h-5 w-5 text-purple-600" />
                        FSI Range & Provisions
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Permitted FSI Range:</span>
                          <span className="font-medium">
                            {fsiData.basic_fsi_analysis.land_use_analysis.permitted_fsi_range?.[0]} - {fsiData.basic_fsi_analysis.land_use_analysis.permitted_fsi_range?.[1]}
                          </span>
                        </div>
                      </div>
                      
                      {fsiData.basic_fsi_analysis.land_use_analysis.special_provisions && (
                        <div className="mt-3">
                          <h5 className="font-medium text-purple-700 mb-2">Special Provisions:</h5>
                          <ul className="space-y-1">
                            {fsiData.basic_fsi_analysis.land_use_analysis.special_provisions.map((provision, index) => (
                              <li key={index} className="text-sm text-purple-600 flex items-start gap-2">
                                <div className="w-1.5 h-1.5 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                                {provision}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {fsiData.basic_fsi_analysis.land_use_analysis.height_restrictions && (
                      <div className="bg-orange-50 rounded-lg p-4">
                        <h4 className="font-semibold mb-2 flex items-center gap-2">
                          <Building className="h-5 w-5 text-orange-600" />
                          Height Restrictions
                        </h4>
                        <div className="text-sm">
                          <div className="flex justify-between">
                            <span>Max Height:</span>
                            <span className="font-medium">
                              {fsiData.basic_fsi_analysis.land_use_analysis.height_restrictions.max_height_m || 'No specific limit'}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'sba-analysis':
        return (
          <div className="space-y-6">
            {fsiData.sba_analysis ? (
              <>
                {/* SBA Overview */}
                <div className="bg-gradient-to-r from-cyan-50 to-teal-50 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Layers className="h-8 w-8 text-cyan-600" />
                    <div>
                      <h3 className="text-2xl font-bold text-gray-800">Super Built-up Area Analysis</h3>
                      <p className="text-gray-600">Comprehensive floor and unit analysis</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                      <div className="text-2xl font-bold text-cyan-600">
                        {convertArea(fsiData.sba_analysis.sba_calculations?.SBA_sqm)}
                      </div>
                      <div className="text-sm text-gray-600 mb-1">Total SBA ({getUnitLabel()})</div>
                      <div className="text-xs text-gray-500">
                        {(fsiData.sba_analysis.sba_calculations?.SBA_sqm ? (fsiData.sba_analysis.sba_calculations.SBA_sqm * 10.7639).toFixed(2) : '0')} sq.ft
                      </div>
                    </div>
                    
                    <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                      <div className="text-2xl font-bold text-green-600">
                        {convertArea(fsiData.sba_analysis.sba_calculations?.saleable_area_sqm)}
                      </div>
                      <div className="text-sm text-gray-600 mb-1">Saleable Area ({getUnitLabel()})</div>
                      <div className="text-xs text-gray-500">
                        {(fsiData.sba_analysis.sba_calculations?.saleable_area_sqm ? (fsiData.sba_analysis.sba_calculations.saleable_area_sqm * 10.7639).toFixed(2) : '0')} sq.ft
                      </div>
                    </div>
                    
                    <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                      <div className="text-2xl font-bold text-purple-600">
                        {fsiData.sba_analysis.floor_analysis?.final_permissible_floors || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-600">Permissible Floors</div>
                    </div>
                    
                    <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                      <div className="text-2xl font-bold text-orange-600">
                        {fsiData.sba_analysis.unit_analysis?.total_units || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-600">Total Units</div>
                    </div>
                  </div>
                </div>

                {/* Area Data Breakdown */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Crop className="h-6 w-6 text-blue-600" />
                    <h3 className="text-xl font-bold text-gray-800">Area Data Breakdown</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">FP Area</div>
                      <div className="text-2xl font-bold text-blue-600">
                        {convertArea(fsiData.sba_analysis.area_data?.fp_area_sqm)} {getUnitLabel()}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {(fsiData.sba_analysis.area_data?.fp_area_sqm ? (fsiData.sba_analysis.area_data.fp_area_sqm * 1.19599).toFixed(2) : '0')} sq.yd
                      </div>
                    </div>

                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">OP Area</div>
                      <div className="text-2xl font-bold text-green-600">
                        {convertArea(fsiData.sba_analysis.area_data?.op_area_sqm)} {getUnitLabel()}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {(fsiData.sba_analysis.area_data?.op_area_sqm ? (fsiData.sba_analysis.area_data.op_area_sqm * 1.19599).toFixed(2) : '0')} sq.yd
                      </div>
                    </div>

                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">Net Developable Area</div>
                      <div className="text-2xl font-bold text-purple-600">
                        {convertArea(fsiData.sba_analysis.area_data?.net_developable_sqm)} {getUnitLabel()}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {(fsiData.sba_analysis.area_data?.net_developable_sqm ? (fsiData.sba_analysis.area_data.net_developable_sqm * 1.19599).toFixed(2) : '0')} sq.yd
                      </div>
                    </div>

                    <div className="bg-orange-50 rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">Retention</div>
                      <div className="text-2xl font-bold text-orange-600">
                        {fsiData.sba_analysis.area_data?.retention_pct?.toFixed(2)}%
                      </div>
                    </div>

                    <div className="bg-red-50 rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">TP Deduction</div>
                      <div className="text-2xl font-bold text-red-600">
                        {fsiData.sba_analysis.area_data?.tp_deduction_pct?.toFixed(2)}%
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floor Analysis */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Building className="h-6 w-6 text-green-600" />
                    <h3 className="text-xl font-bold text-gray-800">Floor Analysis</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-600">Final Permissible Floors</span>
                          <span className="text-xl font-bold text-green-600">
                            {fsiData.sba_analysis.floor_analysis?.final_permissible_floors}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          Based on {fsiData.sba_analysis.floor_analysis?.limiting_factor} constraint
                        </div>
                      </div>

                      <div className="bg-blue-50 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-600">Floors by FSI</span>
                          <span className="text-xl font-bold text-blue-600">
                            {fsiData.sba_analysis.floor_analysis?.floors_by_fsi?.toFixed(1)}
                          </span>
                        </div>
                      </div>

                      <div className="bg-purple-50 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-600">Floors by Height</span>
                          <span className="text-xl font-bold text-purple-600">
                            {fsiData.sba_analysis.floor_analysis?.floors_by_height}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-orange-50 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-600">Building Height</span>
                          <span className="text-xl font-bold text-orange-600">
                            {fsiData.sba_analysis.floor_analysis?.building_height_m} m
                          </span>
                        </div>
                      </div>

                      <div className="bg-yellow-50 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-600">Coverage</span>
                          <span className="text-xl font-bold text-yellow-600">
                            {fsiData.sba_analysis.floor_analysis?.coverage_pct}%
                          </span>
                        </div>
                      </div>

                      <div className="bg-cyan-50 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-600">Final Floorplate Area</span>
                          <span className="text-xl font-bold text-cyan-600">
                            {convertArea(fsiData.sba_analysis.floor_analysis?.final_floorplate_sqm)} {getUnitLabel()}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {(fsiData.sba_analysis.floor_analysis?.final_floorplate_sqm ? (fsiData.sba_analysis.floor_analysis.final_floorplate_sqm * 1.19599).toFixed(2) : '0')} sq.yd
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Info className="h-5 w-5 text-indigo-600" />
                      <span className="font-semibold text-indigo-800">Limiting Factor</span>
                    </div>
                    <p className="text-sm text-indigo-700">
                      The development is limited by <strong>{fsiData.sba_analysis.floor_analysis?.limiting_factor}</strong>.
                      {fsiData.sba_analysis.floor_analysis?.limiting_factor === 'FSI' 
                        ? ' Consider exploring additional FSI options if available.'
                        : ' Height restrictions apply based on local regulations.'}
                    </p>
                  </div>
                </div>

                {/* SBA Calculations */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Calculator className="h-6 w-6 text-purple-600" />
                    <h3 className="text-xl font-bold text-gray-800">SBA Calculations</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                      <div className="text-sm text-purple-600 mb-1">Total SBA</div>
                      <div className="text-2xl font-bold text-purple-700">
                        {convertArea(fsiData.sba_analysis.sba_calculations?.SBA_sqm)} {getUnitLabel()}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {(fsiData.sba_analysis.sba_calculations?.SBA_sqm ? (fsiData.sba_analysis.sba_calculations.SBA_sqm * 10.7639).toFixed(2) : '0')} sq.ft
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                      <div className="text-sm text-blue-600 mb-1">SBA per Floor</div>
                      <div className="text-2xl font-bold text-blue-700">
                        {convertArea(fsiData.sba_analysis.sba_calculations?.SBA_per_floor_sqm)} {getUnitLabel()}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {(fsiData.sba_analysis.sba_calculations?.SBA_per_floor_sqm ? (fsiData.sba_analysis.sba_calculations.SBA_per_floor_sqm * 10.7639).toFixed(2) : '0')} sq.ft
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                      <div className="text-sm text-green-600 mb-1">Saleable Area</div>
                      <div className="text-2xl font-bold text-green-700">
                        {convertArea(fsiData.sba_analysis.sba_calculations?.saleable_area_sqm)} {getUnitLabel()}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {(fsiData.sba_analysis.sba_calculations?.saleable_area_sqm ? (fsiData.sba_analysis.sba_calculations.saleable_area_sqm * 10.7639).toFixed(2) : '0')} sq.ft
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
                      <div className="text-sm text-orange-600 mb-1">Saleable Ratio</div>
                      <div className="text-2xl font-bold text-orange-700">
                        {((fsiData.sba_analysis.sba_calculations?.saleable_ratio || 0) * 100).toFixed(1)}%
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4 border border-yellow-200">
                      <div className="text-sm text-yellow-600 mb-1">Common Area</div>
                      <div className="text-2xl font-bold text-yellow-700">
                        {convertArea(fsiData.sba_analysis.sba_calculations?.common_area_sqm)} {getUnitLabel()}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {(fsiData.sba_analysis.sba_calculations?.common_area_sqm ? (fsiData.sba_analysis.sba_calculations.common_area_sqm * 10.7639).toFixed(2) : '0')} sq.ft
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
                      <div className="text-sm text-red-600 mb-1">Common Area Loading</div>
                      <div className="text-2xl font-bold text-red-700">
                        {fsiData.sba_analysis.sba_calculations?.common_area_loading_pct}%
                      </div>
                    </div>
                  </div>
                </div>

                {/* Unit Analysis */}
                {fsiData.sba_analysis.unit_analysis && (
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Home className="h-6 w-6 text-orange-600" />
                      <h3 className="text-xl font-bold text-gray-800">Unit Analysis</h3>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 text-center border border-orange-200">
                        <div className="text-sm text-orange-600 mb-1">Total Units</div>
                        <div className="text-3xl font-bold text-orange-700">
                          {fsiData.sba_analysis.unit_analysis.total_units}
                        </div>
                      </div>

                      <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-lg p-4 text-center border border-cyan-200">
                        <div className="text-sm text-cyan-600 mb-1">Units per Floor</div>
                        <div className="text-3xl font-bold text-cyan-700">
                          {fsiData.sba_analysis.unit_analysis.units_per_floor}
                        </div>
                      </div>

                      <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-4 text-center border border-indigo-200">
                        <div className="text-sm text-indigo-600 mb-1">Avg Unit Size</div>
                        <div className="text-3xl font-bold text-indigo-700">
                          {convertArea(fsiData.sba_analysis.unit_analysis.avg_unit_size_sqm)} {getUnitLabel()}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          {(fsiData.sba_analysis.unit_analysis.avg_unit_size_sqm ? (fsiData.sba_analysis.unit_analysis.avg_unit_size_sqm * 10.7639).toFixed(2) : '0')} sq.ft
                        </div>
                      </div>
                    </div>

                    {/* Unit Mix Breakdown */}
                    {fsiData.sba_analysis.unit_analysis.units_breakdown && (
                      <div className="space-y-3">
                        <h4 className="font-semibold text-lg text-gray-800 mb-3">Unit Mix Breakdown</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {fsiData.sba_analysis.unit_analysis.units_breakdown.map((unit: any, idx: number) => (
                            <div key={idx} className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <Home className="h-5 w-5 text-gray-600" />
                                  <span className="font-bold text-lg text-gray-800">{unit.type}</span>
                                </div>
                                <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                                  {unit.count} units
                                </span>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                <div className="bg-white rounded p-2">
                                  <div className="text-gray-600 text-xs">Unit Size</div>
                                  <div className="font-semibold text-gray-800">{unit.size_sqm} sqm</div>
                                </div>
                                <div className="bg-white rounded p-2">
                                  <div className="text-gray-600 text-xs">Proportion</div>
                                  <div className="font-semibold text-gray-800">{unit.proportion_pct}%</div>
                                </div>
                                <div className="bg-white rounded p-2 col-span-2">
                                  <div className="text-gray-600 text-xs">Total Saleable Area</div>
                                  <div className="font-semibold text-gray-800">{unit.total_saleable_sqm?.toFixed(2)} sqm</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* FSI Calculations Summary */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Calculator className="h-6 w-6 text-blue-600" />
                    <h3 className="text-xl font-bold text-gray-800">FSI Calculations Summary</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">Base FSI</div>
                      <div className="text-2xl font-bold text-blue-600">
                        {fsiData.sba_analysis.fsi_calculations?.base_fsi}
                      </div>
                    </div>

                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">Permitted Max FSI</div>
                      <div className="text-2xl font-bold text-green-600">
                        {fsiData.sba_analysis.fsi_calculations?.permitted_max_fsi}
                      </div>
                    </div>

                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">FSI Premium</div>
                      <div className="text-2xl font-bold text-purple-600">
                        {fsiData.sba_analysis.fsi_calculations?.fsi_premium_pct}%
                      </div>
                    </div>

                    <div className="bg-orange-50 rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">Gross Buildable</div>
                      <div className="text-2xl font-bold text-orange-600">
                        {convertArea(fsiData.sba_analysis.fsi_calculations?.gross_buildable_sqm)} {getUnitLabel()}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {(fsiData.sba_analysis.fsi_calculations?.gross_buildable_sqm ? (fsiData.sba_analysis.fsi_calculations.gross_buildable_sqm * 10.7639).toFixed(2) : '0')} sq.ft
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
                    <Tag className="h-4 w-4" />
                    <span>Zone Code: <strong>{fsiData.sba_analysis.fsi_calculations?.zone_code}</strong></span>
                  </div>
                </div>

                {/* Assumptions */}
                {fsiData.sba_analysis.assumptions && (
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Settings className="h-6 w-6 text-gray-600" />
                      <h3 className="text-xl font-bold text-gray-800">Analysis Assumptions</h3>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-white rounded-lg p-3 border">
                        <div className="text-xs text-gray-600 mb-1">Coverage</div>
                        <div className="text-lg font-bold text-gray-800">
                          {fsiData.sba_analysis.assumptions.coverage_pct}%
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-3 border">
                        <div className="text-xs text-gray-600 mb-1">Saleable Ratio</div>
                        <div className="text-lg font-bold text-gray-800">
                          {((fsiData.sba_analysis.assumptions.saleable_ratio || 0) * 100).toFixed(0)}%
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-3 border">
                        <div className="text-xs text-gray-600 mb-1">Common Area Loading</div>
                        <div className="text-lg font-bold text-gray-800">
                          {fsiData.sba_analysis.assumptions.common_area_loading_pct}%
                        </div>
                      </div>

                      <div className="bg-white rounded-lg p-3 border">
                        <div className="text-xs text-gray-600 mb-1">Floor Height</div>
                        <div className="text-lg font-bold text-gray-800">
                          {fsiData.sba_analysis.assumptions.typical_floor_height_m} m
                        </div>
                      </div>
                    </div>

                    {fsiData.sba_analysis.assumptions.unit_mix && (
                      <div className="mt-4 bg-white rounded-lg p-4 border">
                        <h4 className="font-semibold text-gray-800 mb-3">Unit Mix Assumptions</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {fsiData.sba_analysis.assumptions.unit_mix.map((mix: any, idx: number) => (
                            <div key={idx} className="bg-gray-50 rounded p-3 flex justify-between items-center">
                              <div>
                                <div className="font-medium text-gray-800">{mix.type}</div>
                                <div className="text-sm text-gray-600">{mix.size_sqm} sqm</div>
                              </div>
                              <div className="text-right">
                                <div className="font-bold text-blue-600">{(mix.proportion * 100).toFixed(0)}%</div>
                                <div className="text-xs text-gray-500">proportion</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <Layers className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-600 mb-2">SBA Analysis Not Available</h3>
                <p className="text-gray-500">SBA and floor analysis data is not available for this plot.</p>
              </div>
            )}
          </div>
        );

      case 'risk':
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Risk Assessment</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Shield className="h-6 w-6 text-red-500" />
                    <h4 className="font-semibold">Overall Risk Profile</h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span>Risk Level:</span>
                      <span className={`font-medium px-3 py-1 rounded-full text-sm ${
                        fsiData.risk_assessment?.risk_level === 'Low' ? 'bg-green-100 text-green-800' :
                        fsiData.risk_assessment?.risk_level === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {fsiData.risk_assessment?.risk_level || 'Unknown'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Risk Score:</span>
                      <span className="font-medium">{fsiData.risk_assessment?.overall_risk_score || 0}/100</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          (fsiData.risk_assessment?.overall_risk_score || 0) < 30 ? 'bg-green-500' :
                          (fsiData.risk_assessment?.overall_risk_score || 0) < 70 ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${fsiData.risk_assessment?.overall_risk_score || 0}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <AlertTriangle className="h-6 w-6 text-orange-500" />
                    <h4 className="font-semibold">Risk Factors</h4>
                  </div>
                  <div className="space-y-2">
                    {fsiData.risk_assessment?.identified_risks?.length ? (
                      fsiData.risk_assessment.identified_risks.map((risk, index) => (
                        <div key={index} className="flex items-start gap-2 text-sm">
                          <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span>{risk.description}</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-sm">No specific risks identified</p>
                    )}
                  </div>
                </div>
              </div>
              
              {fsiData.risk_assessment?.mitigation_priority && fsiData.risk_assessment.mitigation_priority.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-3">Mitigation Priorities</h4>
                  <div className="space-y-2">
                    {fsiData.risk_assessment.mitigation_priority.map((priority, index) => (
                      <div key={index} className="flex items-start gap-2 text-sm">
                        <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span>{priority}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 'recommendations':
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Development Recommendations</h3>
              <div className="space-y-4">
                {fsiData.recommendations && fsiData.recommendations.length > 0 ? (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <TrendingUp className="h-6 w-6 text-green-500" />
                      <h4 className="font-semibold">Recommendations</h4>
                    </div>
                    <div className="space-y-3">
                      {fsiData.recommendations.map((recommendation, index) => (
                        <div key={index} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                          <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-green-600 font-bold text-sm">{index + 1}</span>
                          </div>
                          <div className="text-sm">
                            <div className="font-medium text-green-800 mb-1">{recommendation.category}</div>
                            <div className="space-y-1">
                              {recommendation.actions.map((action, actionIndex) => (
                                <div key={actionIndex} className="text-gray-700">• {action}</div>
                              ))}
                            </div>
                            <div className="mt-2">
                              <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                                recommendation.priority === 'High' ? 'bg-red-100 text-red-800' :
                                recommendation.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {recommendation.priority} Priority
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
                    <p className="text-gray-500">No specific recommendations available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'tall-building':
        return (
          <div className="space-y-6">
            {fsiData.tall_building_analysis ? (
              <>
                {/* Eligibility Overview */}
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Building className="h-8 w-8 text-indigo-600" />
                    <div>
                      <h3 className="text-2xl font-bold text-gray-800">Tall Building Analysis</h3>
                      <p className="text-gray-600">High-rise development potential assessment</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg p-4 text-center">
                      <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-2" />
                      <div className="text-lg font-bold text-green-600">
                        {fsiData.tall_building_analysis.road_width_eligible ? 'Eligible' : 'Not Eligible'}
                      </div>
                      <div className="text-sm text-gray-600">Road Width Eligibility</div>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4 text-center">
                      <Ruler className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                      <div className="text-lg font-bold text-blue-600">{fsiData.tall_building_analysis.primary_road_width?.toFixed(1)}m</div>
                      <div className="text-sm text-gray-600">Primary Road Width</div>
                      <div className="text-xs text-gray-500">Min: {fsiData.tall_building_analysis.minimum_road_width_required}m</div>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4 text-center">
                      <MapPin className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                      <div className="text-lg font-bold text-purple-600">{fsiData.tall_building_analysis.plot_area_sqm?.toLocaleString()}</div>
                      <div className="text-sm text-gray-600">Plot Area (sq.m)</div>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4 text-center">
                      <Eye className="h-6 w-6 text-orange-600 mx-auto mb-2" />
                      <div className="text-lg font-bold text-orange-600">{fsiData.tall_building_analysis.widest_road_width?.toFixed(1)}m</div>
                      <div className="text-sm text-gray-600">Widest Road</div>
                      <div className="text-xs text-gray-500">{fsiData.tall_building_analysis.widest_road_name}</div>
                    </div>
                  </div>
                </div>

                {/* Height Scenarios */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <BarChart3 className="h-6 w-6 text-blue-600" />
                    <h3 className="text-xl font-bold text-gray-800">Height Development Scenarios</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {fsiData.tall_building_analysis.height_scenarios?.map((scenario, index) => (
                      <div key={index} className={`border-2 rounded-lg p-4 ${
                        scenario.viability_rating === 'FEASIBLE' ? 'border-green-200 bg-green-50' :
                        scenario.viability_rating === 'STANDARD' ? 'border-blue-200 bg-blue-50' :
                        scenario.viability_rating === 'CHALLENGING' ? 'border-orange-200 bg-orange-50' :
                        'border-red-200 bg-red-50'
                      }`}>
                        <div className="flex items-center gap-2 mb-3">
                          <Building className={`h-5 w-5 ${
                            scenario.viability_rating === 'FEASIBLE' ? 'text-green-600' :
                            scenario.viability_rating === 'STANDARD' ? 'text-blue-600' :
                            scenario.viability_rating === 'CHALLENGING' ? 'text-orange-600' :
                            'text-red-600'
                          }`} />
                          <span className="font-semibold">{scenario.height_m}m Height</span>
                        </div>
                        
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span>Floors:</span>
                            <span className="font-medium">~{scenario.approximate_floors}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Buildable Area:</span>
                            <span className="font-medium">{scenario.buildable_area_sqm?.toLocaleString()} sq.m</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Estimated FSI:</span>
                            <span className="font-medium">{scenario.estimated_fsi}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Development Cost:</span>
                            <span className="font-medium">₹{(scenario.estimated_development_cost / 10000000)?.toFixed(1)}Cr</span>
                          </div>
                          <div className="flex justify-between">
                            <span>SEOR Requirement:</span>
                            <span className="font-medium">{scenario.seor_requirement}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Height Compliant:</span>
                            <span className={`font-medium ${scenario.height_compliant ? 'text-green-600' : 'text-red-600'}`}>
                              {scenario.height_compliant ? 'Yes' : 'No'}
                            </span>
                          </div>
                        </div>
                        
                        <div className={`mt-3 px-2 py-1 rounded-full text-xs font-medium text-center ${
                          scenario.viability_rating === 'FEASIBLE' ? 'bg-green-200 text-green-800' :
                          scenario.viability_rating === 'STANDARD' ? 'bg-blue-200 text-blue-800' :
                          scenario.viability_rating === 'CHALLENGING' ? 'bg-orange-200 text-orange-800' :
                          'bg-red-200 text-red-800'
                        }`}>
                          {scenario.viability_rating}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tall Building Constraints */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                    <h3 className="text-xl font-bold text-gray-800">Tall Building Constraints</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {fsiData.tall_building_analysis.tall_building_constraints?.map((constraint, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                        <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-red-700">{constraint}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recommendations */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                    <h3 className="text-xl font-bold text-gray-800">Tall Building Recommendations</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {fsiData.tall_building_analysis.recommendations?.map((recommendation, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                        <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-green-700">{recommendation}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <Building className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-600 mb-2">Tall Building Analysis Not Available</h3>
                <p className="text-gray-500">This plot may not be eligible for tall building development or analysis data is not available.</p>
              </div>
            )}
          </div>
        );

      case 'development-scenarios':
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <Target className="h-8 w-8 text-teal-600" />
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">Development Scenarios</h3>
                  <p className="text-gray-600">Strategic development options analysis</p>
                </div>
              </div>
            </div>

            {fsiData.development_scenarios?.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {fsiData.development_scenarios.map((scenario, index) => (
                  <div key={index} className={`border-2 rounded-lg p-6 ${
                    scenario.risk_level === 'LOW' ? 'border-green-200 bg-green-50' :
                    scenario.risk_level === 'MEDIUM' ? 'border-yellow-200 bg-yellow-50' :
                    'border-red-200 bg-red-50'
                  }`}>
                    <div className="flex items-center gap-3 mb-4">
                      <Target className={`h-6 w-6 ${
                        scenario.risk_level === 'LOW' ? 'text-green-600' :
                        scenario.risk_level === 'MEDIUM' ? 'text-yellow-600' :
                        'text-red-600'
                      }`} />
                      <h4 className="text-lg font-bold text-gray-800">{scenario.name}</h4>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-4">{scenario.description}</p>
                    
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Calculator className="h-4 w-4 text-blue-600" />
                        <span className="text-sm">FSI Utilized: <span className="font-medium">{scenario.fsi_utilized}</span></span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Building className="h-4 w-4 text-purple-600" />
                        <span className="text-sm">Height: <span className="font-medium">{scenario.height_m}m</span></span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-orange-600" />
                        <span className="text-sm">Timeline: <span className="font-medium">{scenario.estimated_timeline_months} months</span></span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Shield className={`h-4 w-4 ${
                          scenario.risk_level === 'LOW' ? 'text-green-600' :
                          scenario.risk_level === 'MEDIUM' ? 'text-yellow-600' :
                          'text-red-600'
                        }`} />
                        <span className="text-sm">Risk Level: <span className={`font-medium ${
                          scenario.risk_level === 'LOW' ? 'text-green-600' :
                          scenario.risk_level === 'MEDIUM' ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>{scenario.risk_level}</span></span>
                      </div>
                    </div>
                    
                    <div className={`mt-4 px-3 py-2 rounded-lg text-center ${
                      scenario.name === 'Conservative' ? 'bg-green-100 text-green-800' :
                      scenario.name === 'Optimistic' ? 'bg-blue-100 text-blue-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>
                      <span className="text-sm font-medium">
                        {scenario.name === 'Conservative' ? 'Recommended for Risk-Averse Investors' :
                         scenario.name === 'Optimistic' ? 'Balanced Risk-Return Profile' :
                         'High-Return Premium Development'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Target className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-600 mb-2">No Development Scenarios Available</h3>
                <p className="text-gray-500">Development scenarios data is not available for this plot.</p>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="text-center py-12">
            <p className="text-gray-600">Select a tab to view detailed analysis</p>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
      <div 
        className="w-full bg-white rounded-t-2xl shadow-2xl transform transition-transform duration-300 ease-out animate-in slide-in-from-bottom-full"
        style={{ maxHeight: '95vh' }}
      >
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-[#179246] to-[#147a3c] text-white px-6 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calculator className="h-6 w-6" />
              <div>
                <h2 className="text-xl font-bold">Detailed FSI Analysis</h2>
                <p className="text-emerald-100 text-sm">
                  {fsiData?.plot_information?.name || 'Comprehensive Plot Analysis'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleEnhancedExportPDF}
                disabled={isExportingPDF || !fsiData}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 flex items-center gap-2"
              >
                {isExportingPDF ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Star className="h-4 w-4" />
                )}
                <span className="hidden sm:inline text-sm">
                  {isExportingPDF ? 'Generating...' : 'PDF with Icons'}
                </span>
              </Button>
              <Button
                onClick={handleExportPDF}
                disabled={isExportingPDF || !fsiData}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 flex items-center gap-2"
              >
                {isExportingPDF ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                <span className="hidden sm:inline text-sm">
                  {isExportingPDF ? 'Generating...' : 'Standard PDF'}
                </span>
              </Button>
              <div className="flex items-center gap-2 mr-3">
                <Ruler className="h-4 w-4 text-white" />
                <select
                  value={areaUnit}
                  onChange={(e) => setAreaUnit(e.target.value as AreaUnit)}
                  className="bg-white bg-opacity-20 text-white text-sm rounded-lg px-3 py-1.5 border border-white border-opacity-30 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 cursor-pointer hover:bg-opacity-30 transition-all"
                >
                  <option value="sqm" className="bg-gray-800 text-white">Square Meters (sqm)</option>
                  <option value="sqft" className="bg-gray-800 text-white">Square Feet (sq.ft)</option>
                  <option value="sqyard" className="bg-gray-800 text-white">Square Yards (sq.yd)</option>
                </select>
              </div>
              <Button
                onClick={onClose}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-gray-50 px-6 py-2 border-b border-gray-200">
          <div className="flex space-x-1 overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'bg-[#179246] text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(95vh - 180px)' }}>
          {isLoading ? (
            <LoadingDisplay />
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16 px-8">
              <div className="mb-6">
                <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-800 mb-2">Analysis Failed</h3>
                <p className="text-gray-600 text-center max-w-md">{error}</p>
              </div>
              <Button
                onClick={onClose}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Close
              </Button>
            </div>
          ) : fsiData ? (
            renderTabContent()
          ) : (
            <div className="flex flex-col items-center justify-center py-16 px-8">
              <div className="mb-6">
                <Info className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-600 mb-2">No Data Available</h3>
                <p className="text-gray-500 text-center max-w-md">
                  No FSI analysis data is available to display.
                </p>
              </div>
              <Button
                onClick={onClose}
                variant="outline"
              >
                Close
              </Button>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        {fsiData && !isLoading && !error && (
          <div className="bg-gray-50 px-6 py-4 flex gap-3 rounded-b-2xl border-t border-gray-200">
            <Button 
              onClick={onClose}
              variant="outline" 
              className="flex-1"
            >
              Close
            </Button>
            <Button 
              onClick={() => {
                alert('PDF export feature coming soon!');
              }}
              className="flex-1 bg-[#179246] hover:bg-[#147a3c]"
            >
              <FileText className="h-4 w-4 mr-2" />
              Export Report
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DetailedFSIModal;